import { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { FileText, ExternalLink, Download } from 'lucide-react';
import { toast } from 'sonner';

const getBackendUrl = () => {
  if (window.location.hostname !== 'localhost') {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};

const BACKEND_URL = getBackendUrl();

function Resources() {
  const [loading, setLoading] = useState(true);
  const [resources, setResources] = useState([]);

  useEffect(() => {
    fetchResources();
  }, []);

  const fetchResources = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/resources`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setResources(data);
      }
    } catch (error) {
      toast.error('Failed to load resources');
    } finally {
      setLoading(false);
    }
  };

  const getIcon = (type) => {
    if (type === 'link') {
      return <ExternalLink className="h-5 w-5 text-accent" />;
    }
    return <FileText className="h-5 w-5 text-primary" />;
  };

  const groupedResources = resources.reduce((acc, resource) => {
    const category = resource.category || 'General';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(resource);
    return acc;
  }, {});

  if (loading) {
    return (
      <DashboardLayout title="Resources">
        <div className="flex items-center justify-center h-64">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Resources">
      <div className="space-y-6" data-testid="resources-page">
        <div className="flex items-center gap-3">
          <FileText className="h-8 w-8 text-primary" />
          <h2 className="text-2xl font-mono font-bold">Resources & Documentation</h2>
        </div>

        {resources.length === 0 ? (
          <div className="bg-card border border-border rounded-lg p-12 text-center" data-testid="no-resources-message">
            <FileText className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-mono font-bold mb-2">No Resources Available</h3>
            <p className="text-muted-foreground">Resources will appear here when added by your administrator</p>
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(groupedResources).map(([category, items]) => (
              <div key={category}>
                <h3 className="text-lg font-mono font-bold mb-4 text-primary" data-testid={`category-${category.toLowerCase().replace(/\s+/g, '-')}`}>{category}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {items.map((resource) => (
                    <a
                      key={resource.resource_id}
                      href={resource.url || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-card border border-border hover:border-primary/50 rounded-lg p-6 transition-colors block"
                      data-testid={`resource-card-${resource.resource_id}`}
                    >
                      <div className="flex items-start gap-3 mb-3">
                        {getIcon(resource.type)}
                        <div className="flex-1 min-w-0">
                          <h4 className="font-mono font-bold mb-1" data-testid={`resource-title-${resource.resource_id}`}>{resource.title}</h4>
                          {resource.description && (
                            <p className="text-sm text-muted-foreground line-clamp-2">{resource.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-mono text-muted-foreground">{resource.type.toUpperCase()}</span>
                        {resource.type === 'link' ? (
                          <ExternalLink className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <Download className="h-4 w-4 text-muted-foreground" />
                        )}
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export default Resources;